<template>
  <div class="report-sidebar">
    <div class="header d-flex w-100 align-items-center py-2 px-4">
      <a :href="`/dashboard/template_builder?type=${reportTemplate.type}`" @click.prevent="handleNavigation">
        <v-icon dense color="#3F3F3F" size="24">mdi-arrow-left</v-icon>
      </a>
      <div>
        <template v-if="!isEditingName">
          <template v-if="isTruncated">
            <v-tooltip bottom max-width="280px">
              <template v-slot:activator="{ on, attrs }">
                <h3 ref="reportTitle" class="ma-0 font-bold" v-bind="attrs" v-on="on" style="width: 250px;">
                  {{ truncate(reportTemplate.name || '') }}
                </h3>
              </template>
              <span>{{ reportTemplate.name }}</span>
            </v-tooltip>
          </template>
          <h3 v-else class="ma-0 font-bold" style="width: 250px;">{{ reportTemplate.name }}</h3>
        </template>
        <input v-else v-model.trim="newName" ref="nameInput" class="form-control" type="text" style="width: 250px;" />
        <h6 class="ma-0 mt-2">{{ mappedType }}</h6>
      </div>
      <button v-if="reportTemplate.name !== 'Quick'" class="btn btn-small btn-outline-secondary"
        style="padding: 0 6px 2px 6px; margin-left: auto;" @click="toggleEdit">
        <v-icon small dense color="#3F3F3F">
          <template v-if="isEditingName">mdi-content-save</template>
          <template v-else>mdi-pencil-outline</template>
        </v-icon>
      </button>
    </div>

    <div v-if="isLoading" class="d-flex justify-content-center align-items-center py-4 h-100">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>

    <template v-else>
      <div v-if="!isHideSearch" class="py-4 px-6">
        <!-- Filer & Search -->
        <div class="filter-search">
          <div class="position-relative bg-white w-100">
            <v-icon small class="bg-white position-absolute p-2" style="right: 0.5rem; top: 4px;"
              color="rgba(79, 79, 79, 1)">mdi-magnify</v-icon>
            <CInput placeholder="Search Column by Title" type="text" v-model="search" @input="fetchFilteredColumns">
            </CInput>
          </div>
          <p class="ma-0 mt-2">{{ infoText }}</p>
        </div>
      </div>
      <div v-if="!isHideSearch" class="category-list">
        <div class="px-6 d-flex flex-wrap" style="gap: 8px;">
          <button :class="[
            'btn',
            'btn-sm',
            activeCategory === 'all' ? 'btn-primary' : 'btn-outline-secondary'
          ]" @click="setActiveCategory('all')">
            All
          </button>
          <button v-for="category in columnList" :key="category.key" :class="[
            'btn',
            'btn-sm',
            category.key === activeCategory ? 'btn-primary' : 'btn-outline-secondary'
          ]" @click="setActiveCategory(category.key)">
            {{ category.name }}
          </button>
        </div>
      </div>
      <!-- Column List -->
      <ColumnList />
    </template>

  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import CInput from '../../c-input.vue'
import ColumnList from './ColumnList.vue'
import _ from 'lodash'

export default {
  components: {
    CInput,
    ColumnList,
  },
  data() {
    return {
      search: '',
      activeCategory: 'all',
      categories: [
        { id: 'all', name: 'All', active: true },
        { id: 'client_detail', name: 'Client Details', active: false },
        { id: 'stickers', name: 'Stickers', active: false },
        { id: 'client_profile', name: 'Client Profile', active: false },
        { id: 'case_detail', name: 'Case Detail', active: false },
        { id: 'client_occupation', name: 'Client Occupation', active: false },
      ],
      isEditingName: false,
      newName: '',
      isLoading: false,
    }
  },
  computed: {
    ...mapState({
      selectedColumns: (state) => state.reportTemplates.selectedColumns
    }),
    ...mapGetters({
      reportColumns: "reportTemplates/reportColumns",
      reportTemplate: "reportTemplates/reportTemplate",
      allReportFields: "reportTemplates/allReportFields",
      hasUnsavedChanges: "reportTemplates/hasUnsavedChanges",
      unsavedChanges: "reportTemplates/unsavedChanges",
      fieldPagination: 'reportTemplates/fieldPagination',
    }),
    mappedType() {
      const typeMapping = {
        'Report::Case': 'Case',
        'Report::Client': 'Client',
        'Report::Ticket': 'Ticket',
        'Report::Join': 'Join (Cases & Clients)',
        'Report::Sales': 'Sales',
      };
      return typeMapping[this.reportTemplate.type] || this.reportTemplate.type;
    },
    infoText() {
      const sharedText = "In the 'All' category, you can search for regular columns (e.g., case details, case name, client name). To search for Stickers, Task Dictionary, or Custom Attribute columns, navigate to the specific category and use the search bar there.";

      const infoMapping = {
        Client: "In the 'All' category, you can search for default columns (e.g., case details, case name, client name). To search for Stickers, or Custom Attribute columns, navigate to the specific category and use the search bar there.",
        Case: sharedText,
        Join: sharedText,
      };
      return infoMapping[this.mappedType] || "In the 'All' category, you can search for regular columns (e.g., case details, case name, client name). To search for other specific columns, navigate to the relevant category and use the search bar there.";
    },
    columnList() {
      let columns;

      if (this.reportTemplate?.column_list?.case_columns && this.reportTemplate?.column_list?.client_columns) {
        columns = [...this.reportTemplate.column_list.case_columns, ...this.reportTemplate.column_list.client_columns];
      } else {
        columns = this.reportTemplate?.column_list || [];
      }

      // Hide case_business_unit for Join reports
      if (this.reportTemplate?.type === 'Report::Join') {
        columns = columns.filter(col => col.key !== 'case_business_unit');
      }

      // Add "(Client)" suffix only for non-Client reports, and only once.
      columns = columns.map(col => {
        const shouldAppend = this.reportTemplate?.type !== 'Report::Client' &&
          ['client_business_unit', 'business_unit'].includes(col.key);

        if (!shouldAppend) return col;

        // Strip any existing "(Client)" tokens to avoid duplicates, then append once.
        const baseName = String(col.name || '').replace(/\s*\(Client\)/gi, '').trim();
        return { ...col, name: `${baseName} (Client)` };
      });

      return columns;
    },
    isHideSearch() {
      return this.reportTemplate.type === 'Report::Ticket' || this.reportTemplate.type === 'Report::Sales';
    },
    isTruncated() {
      return (this.reportTemplate.name || '').length > 22;
    },
  },

  mounted() {
    this.isLoading = true;
    this.$store.dispatch('reportTemplates/getReportColumns', { id: this.$route.params.id })
      .then(() => {
        this.ensureDataFetched().then(() => {
          this.isLoading = false;
        })
      })
    this.$store.commit('reportTemplates/setIsLoading', true);
    this.$store.dispatch('reportTemplates/getAllReportFields', this.$route.params.id)
      .then(() => {
        this.$store.commit('reportTemplates/processSelectedColumns', this.allReportFields);
        this.$store.commit('reportTemplates/setIsLoading', false);
      })
    this.setPagination({
      current_page: 1,
      per_page: this.fieldPagination.per_page,
    });
    this.$store.dispatch('reportTemplates/getReportFields', this.$route.params.id)
    this.$store.dispatch('reportTemplates/getProcedureListFilter')
  },
  methods: {
    ...mapMutations({
      setPagination: 'reportTemplates/setFieldPagination',
    }),
    async ensureDataFetched() {
      for (const group of this.reportColumns) {
        if (!group.data) {
          await this.$store.dispatch('reportTemplates/fetchMissingData', {
            groupKey: group.key,
          })
        }
      }
    },
    fetchFilteredColumns: _.debounce(function () {
      let filter = {};

      if (this.search) {
        filter.query = this.search;
      }

      if (this.activeCategory !== 'all' || this.search) {
        // Jika kategori bukan 'all' atau ada query, tambahkan category ke filter
        filter.category = this.activeCategory !== 'all' ? this.activeCategory : 'all';
      }

      // Jika query dihapus dan category 'all', kosongkan filter agar request tetap berjalan tanpa params
      if (!this.search && this.activeCategory === 'all') {
        filter = {};
      }

      this.$store.dispatch('reportTemplates/getReportColumns', {
        id: this.$route.params.id,
        filter
      }).then(() => {
        this.ensureDataFetched();
      });
    }, 500),
    setActiveCategory(categoryId) {
      this.activeCategory = categoryId;
      this.fetchFilteredColumns();
    },
    handleNavigation(event) {
      const redirectPath = event.currentTarget.href;

      if (this.hasUnsavedChanges || this.unsavedChanges) {
        this.$store.commit('reportTemplates/setConfirmShow', {
          isShow: true,
          type: 'changes',
          title: 'Unsaved Changes',
          description: "Looks like you've made some changes. Don't forget to save before you go!",
          redirect: redirectPath,
        });
      } else {
        window.location.href = event.currentTarget.href;
      }
    },
    truncate(value) {
      if (!value) return '';
      return value.length > 22 ? value.substring(0, 22) + '...' : value;
    },
    toggleEdit() {
      if (this.isEditingName && this.newName !== this.reportTemplate.name) {
        const trimmedName = this.newName.trim();

        if (trimmedName.toLowerCase() === "quick") {
          this.$store.commit('reportTemplates/setBannerFailed', `"Quick" cannot be used as report name because it is reserved for the system's default template.`);
          this.isEditingName = false;
          return;
        }

        const data = {
          id: this.reportTemplate.id,
          payload: {
            report_template: {
              name: this.newName
            }
          }
        }
        this.$store.dispatch('reportTemplates/updateReportTemplate', data).then(() => {
          this.$store.dispatch('reportTemplates/getReportTemplateById', this.$route.params.id);
        })
      }
      this.isEditingName = !this.isEditingName;
      this.newName = this.reportTemplate.name || '';

      this.$nextTick(() => {
        if (this.isEditingName) {
          const input = this.$refs.nameInput;
          if (input) {
            input.focus();
            input.setSelectionRange(this.newName.length, this.newName.length);
          }
        }
      });
    },
  },
  created() {

  },
};
</script>

<style lang="scss" scoped>
.report-sidebar {
  min-width: 380px;
  width: 380px;
  height: 100%;
  background-color: #FFFFFF;
  border-right: 1px solid #D7D7D7;
  display: flex;
  flex-direction: column;
  z-index: 9;

  .header {
    gap: 20px;
    height: 64px;
    border-bottom: 1px solid #D7D7D7;

    h3 {
      font-size: 20px;
      color: #3F3F3F;
    }

    h6 {
      font-size: 14px;
      color: #3F3F3F;
    }
  }

  .column-header {
    background-color: #F4F4F4;
  }

  .column-group {
    margin-bottom: 16px;
  }

  .column-group:last-child {
    margin-bottom: 8px;
  }

  .scroll-container {
    flex: 1;
    overflow-y: auto;
    padding: 0 8px 0 16px;
    margin-top: 16px;
  }

  .category-list {
    max-height: 180px;
    overflow: hidden auto;
  }

  ::v-deep .btn-outline-secondary {
    color: #3F3F3F !important;
    border-color: #D7D7D7 !important;
  }

  ::v-deep .btn-outline-secondary:hover {
    color: #FFFFFF !important;
    border-color: #FFFFFF !important;
  }
}
</style>
